/**
 * Секция «Детектор неподвижности» (/admin/acc-still):
 * метка, неподвижная дольше заданного окна, генерирует событие IMMOBILIZED
 */

import React, { FC, useContext, useEffect } from 'react';
import { connect } from 'react-redux';
import { Button, Card, Form, InputNumber, Space, Typography } from 'antd';
import { ReloadOutlined, SaveOutlined } from '@ant-design/icons';

import { Dispatch, iRootState } from '../../../store';
import { useAsync } from '../../../hooks';
import { IAccStillSettings } from '../../../models/types';

import Spinner from '../../../components_/Spinner';
import EmptyState from '../../../components_/EmptyState';
import { ToastContext } from '../../../components_/Toast';

import { describeAccStillSaveFailure, isAccStillSettings } from './utils';

const mapState = (state: iRootState) => ({
  accStill: state.settingServices.accStill,
  accStillLoading: state.settingServices.accStillLoading
});

const mapDispatch = (dispatch: any) => {
  const d = dispatch as Dispatch;

  return {
    getAccStillSettings: d.settingServices.getAccStillSettings,
    saveAccStillSettings: d.settingServices.saveAccStillSettings,
    setAccStill: d.settingServices.setAccStill,
    reset: d.settingServices.reset
  };
};

type ConnectedProps = ReturnType<typeof mapState> & ReturnType<typeof mapDispatch>;

const AccStillSection: FC<ConnectedProps> = ({
  accStill,
  accStillLoading,
  getAccStillSettings,
  saveAccStillSettings,
  setAccStill,
  reset
}) => {
  const [form] = Form.useForm();
  const toast = useContext(ToastContext);

  const [runGetAccStillSettings, isLoadingGetAccStillSettings] = useAsync(getAccStillSettings);
  const [runSaveAccStillSettings, isLoadingSaveAccStillSettings] = useAsync(saveAccStillSettings);

  useEffect(() => {
    // BFF отдаёт Cache-Control: no-store — читаем при каждом открытии вкладки
    runGetAccStillSettings();

    return () => {
      // reset общий для вкладки (идемпотентен, вызывается из обеих секций)
      reset();
    };
  }, []);

  useEffect(() => {
    if (accStill) {
      form.setFieldsValue(accStill);
    }
  }, [accStill, form]);

  const handleReload = () => {
    runGetAccStillSettings();
  };

  const handleSave = async () => {
    let values: IAccStillSettings;

    try {
      values = await form.validateFields();
    } catch {
      return;
    }

    // Эффект возвращает либо ответ (2xx), либо ошибку axios со статусом в response.
    // Успешный ответ тоже truthy — флаг isError из useAsync не используем
    const result: any = await runSaveAccStillSettings(values);
    const response = result?.isAxiosError ? result.response : result;
    const status: number | undefined = response?.status;

    if (status === 200 || status === 202) {
      // Тело ответа = применённое окно, может отличаться от отправленного
      if (isAccStillSettings(response.data)) {
        form.setFieldsValue(response.data);
        // Держим модель в актуальном состоянии на случай будущих читателей
        setAccStill(response.data);
      }
      if (status === 202) {
        // Применено только в памяти INS: предупреждение, а не обычный успех
        toast({ text: 'Сохранено в памяти, действует до перезапуска сервиса', type: 'default' });
      } else {
        toast({ text: 'Настройки сохранены', type: 'success' });
      }
      return;
    }

    toast({ text: describeAccStillSaveFailure(status, response?.data), type: 'error' });
  };

  const isLoading = accStillLoading || isLoadingGetAccStillSettings || isLoadingSaveAccStillSettings;

  const renderBody = () => {
    if (isLoading) {
      return <Spinner />;
    }

    if (!accStill) {
      return <EmptyState variant="serverError" />;
    }

    return (
      <Form form={form} layout="vertical" style={{ maxWidth: 480 }}>
        <Form.Item
          name="windowSeconds"
          label="Окно неподвижности, сек (windowSeconds)"
          extra="Значение может быть дробным, например 12.5"
          rules={[
            { required: true, message: 'Обязательное поле' },
            {
              validator: async (_: any, value: number) => {
                if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) {
                  throw new Error('Укажите положительное число секунд');
                }
              }
            }
          ]}
        >
          <InputNumber min={0} step={1} style={{ width: '100%' }} />
        </Form.Item>
      </Form>
    );
  };

  return (
    <Card>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
          <Typography.Title level={4} style={{ margin: 0 }}>
            Детектор неподвижности
          </Typography.Title>
          <Space>
            <Button icon={<ReloadOutlined />} onClick={handleReload} disabled={isLoading}>
              Обновить
            </Button>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              onClick={handleSave}
              disabled={isLoading || !accStill}
            >
              Сохранить
            </Button>
          </Space>
        </Space>
        {renderBody()}
      </Space>
    </Card>
  );
};

const withConnect = connect(mapState, mapDispatch);

export default withConnect(AccStillSection);
